import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import orderService from '../services/orderService';
import quoteService from '../services/quoteService';
import POTemplate from '../components/orders/POTemplate';
import QuoteTemplate from '../components/orders/QuoteTemplate';
import { mapOrderToPOData } from '../components/orders/PODataMapper';
import { mapOrderToQuoteData } from '../components/orders/QuoteDataMapper';

const OrderDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Purchase Order State'leri
  const [showPurchaseOrder, setShowPurchaseOrder] = useState(false);
  const [poData, setPoData] = useState(null);
  const [savingPO, setSavingPO] = useState(false);
  const [latestPONumber, setLatestPONumber] = useState(null);

  // 🔥 YENİ: Quote State'leri
  const [showQuote, setShowQuote] = useState(false);
  const [quoteData, setQuoteData] = useState(null);
  const [savingQuote, setSavingQuote] = useState(false);
  const [latestQuoteNumber, setLatestQuoteNumber] = useState(null);

  // 🔥 OPTİMİZE: useCallback ile fonksiyonlar
  const loadOrderDetail = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const orderData = await orderService.getOrderById(id);
      setOrder(orderData);
    } catch (err) {
      console.error('❌ Order detayı yüklenirken hata:', err);
      setError('Sipariş detayı yüklenirken bir hata oluştu: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, [id]);

  // 🔥 MEVCUT: Purchase Order Fonksiyonu
  const handleGeneratePurchaseOrder = useCallback(() => {
    if (!order) return;
    const generatedPOData = mapOrderToPOData(order);
    setPoData(generatedPOData);
    setShowPurchaseOrder(true);
  }, [order]);

  // 🔥 YENİ: Quote Fonksiyonu - Purchase Order'daki gibi
  const handleGenerateQuote = useCallback(() => {
    if (!order) return;

    console.log('🔄 Quote oluşturuluyor...', {
      orderId: order.id,
      orderNumber: order.orderNumber
    });

    // ⭐ Bu order için kaç quote var? (API'den al veya state tut)
    // Şimdilik 1'den başlat, daha sonra API'den alacağız
    const quoteSequence = 1;

    // ⭐ -001 ile birlikte oluştur
    const generatedQuoteData = mapOrderToQuoteData(order, "JH", quoteSequence);

    setQuoteData(generatedQuoteData);
    setShowQuote(true);

    console.log('✅ Quote data hazır (-001 ile):', generatedQuoteData.poNumber);
  }, [order]);

  const handlePODataUpdate = useCallback((newData) => {
    setPoData(prev => ({ ...prev, ...newData }));
  }, []);

  // 🔥 YENİ: Quote Data Update Fonksiyonu
  const handleQuoteDataUpdate = useCallback((newData) => {
    setQuoteData(prev => ({ ...prev, ...newData }));
  }, []);

  // 🔥 MEVCUT: Purchase Order PDF Download
  const handleDownloadPO = useCallback(async () => {
    if (!poData || !order) {
      alert('❌ PO data veya order bilgisi eksik!');
      return;
    }

    setSavingPO(true);

    try {
      console.log('💾 PO Data database\'e PDF ile kaydediliyor...', {
        orderId: order.id,
        poNumber: poData.poNumber
      });

      const saveResult = await orderService.savePurchaseOrderWithPDF(poData, order.id);

      console.log('✅ PO database\'e kaydedildi:', saveResult);

      if (saveResult.success && saveResult.poNumber) {
        setLatestPONumber(saveResult.poNumber);
      }

      if (saveResult.success) {
        const element = document.createElement('a');
        const file = new Blob([document.getElementById('purchase-order').innerHTML], { type: 'text/html' });
        element.href = URL.createObjectURL(file);
        element.download = `${saveResult.poNumber || poData.poNumber}.html`;
        document.body.appendChild(element);
        element.click();
        document.body.removeChild(element);

        alert(`✅ Purchase Order başarıyla oluşturuldu!\n📄 PDF/HTML indirildi\n💾 Database'e kaydedildi\n\nPO Number: ${saveResult.poNumber || poData.poNumber}`);
      } else {
        throw new Error(saveResult.message);
      }

    } catch (error) {
      console.error('❌ PO kaydedilirken hata:', error);
      alert('❌ Purchase Order kaydedilirken bir hata oluştu: ' + error.message);
    } finally {
      setSavingPO(false);
    }
  }, [poData, order]);


  // 🔥 YENİ: Quote PDF Download - TÜM RESPONSE FORMATLARINI HANDLE ET
  const handleDownloadQuote = useCallback(async () => {
    if (!quoteData || !order) {
      alert('❌ Quote data veya order bilgisi eksik!');
      return;
    }

    setSavingQuote(true);

    try {
      console.log('🚀 Backend\'e quote gönderiliyor...', {
        orderId: order.id,
        poNumber: quoteData.poNumber // JH-1225-1329-001
      });

      // ⭐ PO Number'ı OLDUĞU GİBİ gönder (kullanıcı değiştirmiş olabilir)
      const requestData = { ...quoteData };

      const response = await quoteService.createQuoteWithPDF(requestData, order.id);

      console.log('📦 Backend yanıtı:', response);

      // Backend'in döndüğü PO Number'ı kullan (conflict varsa değiştirmiş olabilir)
      let finalPoNumber = null;

      if (response && response.poNumber) {
        finalPoNumber = response.poNumber;
      } else if (response && response.success && response.poNumber) {
        finalPoNumber = response.poNumber;
      } else {
        // Fallback: Frontend'deki poNumber
        finalPoNumber = quoteData.poNumber;
      }

      console.log('✅ Final PO Number:', finalPoNumber);

      // HTML indir
      const fileName = `${finalPoNumber}.html`;
      const element = document.createElement('a');
      const file = new Blob([document.getElementById('quote-template').innerHTML], {
        type: 'text/html'
      });
      element.href = URL.createObjectURL(file);
      element.download = fileName;
      document.body.appendChild(element);
      element.click();
      document.body.removeChild(element);

      alert(`✅ Quote başarıyla oluşturuldu!\n\n` +
        `📄 Dosya: ${fileName}\n` +
        `📋 PO No: ${finalPoNumber}\n\n` +
        `💾 Database'e kaydedildi.`);

      setShowQuote(false);

    } catch (error) {
      console.error('❌ Quote hatası:', error);
      alert('❌ Hata: ' + error.message);
    } finally {
      setSavingQuote(false);
    }
  }, [quoteData, order]);

  // 🔥 OPTİMİZE: useEffect dependency
  useEffect(() => {
    loadOrderDetail();
  }, [loadOrderDetail]);

  // Helper fonksiyonlar
  const getStatusColor = useCallback((status) => {
    const statusColors = {
      'paid': '#4caf50', 'pending': '#ff9800', 'cancelled': '#f44336',
      'refunded': '#9e9e9e', 'fulfilled': '#2196f3'
    };
    return statusColors[status] || '#666';
  }, []);

  const getStatusText = useCallback((status) => {
    const statusTexts = {
      'paid': 'Ödendi', 'pending': 'Beklemede', 'cancelled': 'İptal Edildi',
      'refunded': 'İade Edildi', 'fulfilled': 'Tamamlandı'
    };
    return statusTexts[status] || status;
  }, []);

  const formatCurrency = useCallback((amount) => {
    return new Intl.NumberFormat('tr-TR', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  }, []);

  const calculateOrderTotal = useCallback(() => {
    return order?.items?.reduce((total, item) => total + (item.totalPrice || 0), 0) || 0;
  }, [order]);

  const calculateTotalItems = useCallback((items) => {
    return items?.reduce((total, item) => total + (item.quantity || 0), 0) || 0;
  }, []);

  const getCustomerFullName = useCallback((order) => {
    if (order.customerFirstName && order.customerLastName) {
      return `${order.customerFirstName} ${order.customerLastName}`;
    }
    return order.customerFirstName || order.customerLastName || 'Müşteri';
  }, []);

  // Loading state
  if (loading) {
    return (
      <div style={{ padding: '1rem', paddingBottom: '80px', textAlign: 'center' }}>
        <div style={{ padding: '2rem', fontSize: '1.2rem', color: '#666' }}>
          ⏳ Sipariş detayı yükleniyor...
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div style={{ padding: '1rem', paddingBottom: '80px' }}>
        <div style={{ padding: '2rem', color: '#f44336', backgroundColor: '#ffebee', borderRadius: '8px', marginBottom: '1rem', textAlign: 'center' }}>
          ❌ {error}
        </div>
        <div style={{ textAlign: 'center' }}>
          <button onClick={loadOrderDetail} style={{ padding: '0.5rem 1rem', backgroundColor: '#1976d2', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', marginRight: '1rem' }}>
            🔄 Tekrar Dene
          </button>
          <Link to="/orders" style={{ padding: '0.5rem 1rem', backgroundColor: '#f5f5f5', color: '#333', border: '1px solid #ddd', borderRadius: '4px', textDecoration: 'none', display: 'inline-block' }}>
            ← Sipariş Listesine Dön
          </Link>
        </div>
      </div>
    );
  }

  // Order not found
  if (!order) {
    return (
      <div style={{ padding: '1rem', paddingBottom: '80px', textAlign: 'center' }}>
        <div style={{ padding: '2rem', color: '#666', backgroundColor: '#f5f5f5', borderRadius: '8px' }}>
          <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📭</div>
          <h3>Sipariş bulunamadı</h3>
          <p>ID: {id} numaralı sipariş bulunamadı veya silinmiş olabilir.</p>
          <Link to="/orders" style={{ padding: '0.5rem 1rem', backgroundColor: '#1976d2', color: 'white', border: 'none', borderRadius: '4px', textDecoration: 'none', display: 'inline-block' }}>
            ← Sipariş Listesine Dön
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: '1rem', paddingBottom: '80px', minHeight: '100vh', backgroundColor: '#f5f5f5' }}>
      {/* HEADER */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '2rem', flexWrap: 'wrap', gap: '1rem' }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '0.5rem' }}>
            <Link to="/orders" style={{ color: '#1976d2', textDecoration: 'none', fontSize: '1.5rem' }}>←</Link>
            <h1 style={{ margin: 0, color: '#1976d2', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>📦 Sipariş Detayı</h1>
          </div>
          <p style={{ margin: 0, color: '#666', fontSize: '1.1rem' }}>#{order.orderNumber} - {getCustomerFullName(order)}</p>
        </div>

        <div style={{ textAlign: 'right' }}>
          <span style={{ backgroundColor: getStatusColor(order.status), color: 'white', padding: '0.5rem 1rem', borderRadius: '20px', fontSize: '0.9rem', fontWeight: 'bold', display: 'inline-block', marginBottom: '0.5rem' }}>
            {getStatusText(order.status)}
          </span>
          <p style={{ margin: 0, fontSize: '0.9rem', color: '#666' }}>
            📅 {new Date(order.orderDate).toLocaleDateString('tr-TR', { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
          </p>
        </div>
      </div>

      {/* ORDER INFO GRID */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '1.5rem', marginBottom: '2rem' }}>
        {/* Customer Information */}
        <div style={{ backgroundColor: 'white', padding: '1.5rem', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
          <h3 style={{ margin: '0 0 1rem 0', color: '#333', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>👤 Müşteri Bilgileri</h3>
          <div style={{ lineHeight: '1.6' }}>
            {(order.customerFirstName || order.customerLastName) ? (
              <p style={{ margin: '0.5rem 0' }}><strong>Adı Soyadı:</strong> {[order.customerFirstName, order.customerLastName].filter(Boolean).join(' ')}</p>
            ) : (
              <p style={{ margin: '0.5rem 0', color: '#666', fontStyle: 'italic' }}><strong>Adı Soyadı:</strong> Müşteri bilgisi mevcut değil</p>
            )}
            <p style={{ margin: '0.5rem 0' }}><strong>Email:</strong> {order.customerEmail || 'Mevcut değil'}</p>
            <p style={{ margin: '0.5rem 0' }}><strong>Sipariş No:</strong> #{order.orderNumber}</p>
            <p style={{ margin: '0.5rem 0' }}><strong>Shopify ID:</strong> {order.shopifyOrderId}</p>
          </div>
        </div>

        {/* Order Summary */}
        <div style={{ backgroundColor: 'white', padding: '1.5rem', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
          <h3 style={{ margin: '0 0 1rem 0', color: '#333', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>📊 Sipariş Özeti</h3>
          <div style={{ lineHeight: '1.6' }}>
            <p style={{ margin: '0.5rem 0' }}><strong>Toplam Ürün:</strong> {calculateTotalItems(order.items)} adet</p>
            <p style={{ margin: '0.5rem 0' }}><strong>Toplam Tutar:</strong> {formatCurrency(calculateOrderTotal())}</p>
            <p style={{ margin: '0.5rem 0' }}><strong>Durum:</strong> {getStatusText(order.status)}</p>
            <p style={{ margin: '0.5rem 0' }}><strong>Sipariş Tarihi:</strong> {new Date(order.orderDate).toLocaleDateString('tr-TR')}</p>
          </div>
        </div>
      </div>

      {/* ORDER ITEMS */}
      <div style={{ backgroundColor: 'white', padding: '1.5rem', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)', marginBottom: '2rem' }}>
        <h3 style={{ margin: '0 0 1.5rem 0', color: '#333', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>📦 Sipariş Ürünleri ({order.items?.length || 0})</h3>

        {order.items && order.items.length > 0 ? (
          <div>
            {order.items.map((item, index) => (
              <div key={item.id || index} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', padding: '1rem', backgroundColor: index % 2 === 0 ? '#f9f9f9' : 'white', borderRadius: '8px', marginBottom: '0.5rem', border: '1px solid #f0f0f0' }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 'bold', fontSize: '1rem', marginBottom: '0.5rem', color: '#333' }}>{item.productTitle}</div>
                  {item.variantTitle && (<div style={{ fontSize: '0.9rem', color: '#666', marginBottom: '0.25rem' }}><strong>Varyant:</strong> {item.variantTitle}</div>)}
                  {item.sku && (<div style={{ fontSize: '0.9rem', color: '#666', marginBottom: '0.25rem' }}><strong>SKU:</strong> {item.sku}</div>)}
                  {item.shopifyProductId && (<div style={{ fontSize: '0.8rem', color: '#999' }}><strong>Shopify ID:</strong> {item.shopifyProductId}</div>)}
                </div>
                <div style={{ textAlign: 'right', minWidth: '120px' }}>
                  <div style={{ fontSize: '1rem', fontWeight: 'bold', marginBottom: '0.25rem' }}>{item.quantity} × {formatCurrency(item.unitPrice)}</div>
                  <div style={{ fontSize: '1.1rem', color: '#4caf50', fontWeight: 'bold' }}>{formatCurrency(item.totalPrice)}</div>
                </div>
              </div>
            ))}

            {/* Order Total */}
            <div style={{ borderTop: '2px solid #e0e0e0', paddingTop: '1rem', marginTop: '1rem', textAlign: 'right' }}>
              <div style={{ fontSize: '1.2rem', fontWeight: 'bold', color: '#333' }}>Toplam Sipariş Tutarı: {formatCurrency(calculateOrderTotal())}</div>
            </div>
          </div>
        ) : (
          <div style={{ textAlign: 'center', padding: '2rem', color: '#666' }}>
            <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📦</div>
            <p>Bu siparişte ürün bulunmuyor.</p>
          </div>
        )}
      </div>

      {/* ACTION BUTTONS */}
      <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
        <button
          onClick={() => navigate('/orders')}
          style={{
            padding: '0.75rem 1.5rem',
            backgroundColor: '#f5f5f5',
            color: '#333',
            border: '1px solid #ddd',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '0.9rem'
          }}
        >
          ← Sipariş Listesine Dön
        </button>

        {/* Purchase Order Butonu */}
        <button
          onClick={handleGeneratePurchaseOrder}
          style={{
            padding: '0.75rem 1.5rem',
            backgroundColor: '#ff9800',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '0.9rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}
        >
          📄 Purchase Order
        </button>

        {/* Quote Butonu */}
        <button
          onClick={handleGenerateQuote}
          style={{
            padding: '0.75rem 1.5rem',
            backgroundColor: '#9c27b0',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '0.9rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}
        >
          💰 Generate Quote
        </button>

        <button
          onClick={loadOrderDetail}
          style={{
            padding: '0.75rem 1.5rem',
            backgroundColor: '#1976d2',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '0.9rem'
          }}
        >
          🔄 Bilgileri Yenile
        </button>
      </div>

      {/* PURCHASE ORDER MODAL */}
      {showPurchaseOrder && poData && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.8)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'flex-start',
          zIndex: 10000,
          padding: '20px',
          overflowY: 'auto'
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            width: '100%',
            maxWidth: '1200px',
            maxHeight: '90vh',
            overflow: 'auto',
            position: 'relative'
          }}>
            {/* Modal Header */}
            <div style={{
              padding: '1rem',
              borderBottom: '1px solid #e0e0e0',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              backgroundColor: '#f5f5f5',
              position: 'sticky',
              top: 0,
              zIndex: 100
            }}>
              <h3 style={{ margin: 0, color: '#333' }}>
                📄 Purchase Order - #{poData.poNumber}
              </h3>
              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                {savingPO && (
                  <span style={{
                    fontSize: '0.8rem',
                    color: '#ff9800',
                    marginRight: '0.5rem'
                  }}>
                    💾 Kaydediliyor...
                  </span>
                )}
                <button
                  onClick={handleDownloadPO}
                  disabled={savingPO}
                  style={{
                    padding: '0.5rem 1rem',
                    backgroundColor: savingPO ? '#ccc' : '#4caf50',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: savingPO ? 'not-allowed' : 'pointer',
                    fontSize: '0.8rem'
                  }}
                >
                  {savingPO ? '⏳ İşleniyor...' : '⬇️ PDF İndir & Kaydet'}
                </button>
                <button
                  onClick={() => setShowPurchaseOrder(false)}
                  disabled={savingPO}
                  style={{
                    padding: '0.5rem 1rem',
                    backgroundColor: savingPO ? '#ccc' : '#f44336',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: savingPO ? 'not-allowed' : 'pointer',
                    fontSize: '0.8rem'
                  }}
                >
                  ✕ Kapat
                </button>
              </div>
            </div>

            {/* Purchase Order Content */}
            <div style={{ padding: '0' }}>
              <div id="purchase-order">
                <POTemplate
                  poData={poData}
                  isEditable={true}
                  onDataChange={handlePODataUpdate}
                  currentPONumber={latestPONumber}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* QUOTE MODAL */}
      {showQuote && quoteData && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.8)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'flex-start',
          zIndex: 10000,
          padding: '20px',
          overflowY: 'auto'
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            width: '100%',
            maxWidth: '1200px',
            maxHeight: '90vh',
            overflow: 'auto',
            position: 'relative'
          }}>
            {/* Modal Header */}
            <div style={{
              padding: '1rem',
              borderBottom: '1px solid #e0e0e0',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              backgroundColor: '#f5f5f5',
              position: 'sticky',
              top: 0,
              zIndex: 100
            }}>
              <h3 style={{ margin: 0, color: '#333' }}>
                💰 Quote
              </h3>
              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                {savingQuote && (
                  <span style={{
                    fontSize: '0.8rem',
                    color: '#ff9800',
                    marginRight: '0.5rem'
                  }}>
                    💾 Kaydediliyor...
                  </span>
                )}
                <button
                  onClick={handleDownloadQuote}
                  disabled={savingQuote}
                  style={{
                    padding: '0.5rem 1rem',
                    backgroundColor: savingQuote ? '#ccc' : '#9c27b0',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: savingQuote ? 'not-allowed' : 'pointer',
                    fontSize: '0.8rem'
                  }}
                >
                  {savingQuote ? '⏳ İşleniyor...' : '⬇️ PDF İndir & Kaydet'}
                </button>
                <button
                  onClick={() => setShowQuote(false)}
                  disabled={savingQuote}
                  style={{
                    padding: '0.5rem 1rem',
                    backgroundColor: savingQuote ? '#ccc' : '#f44336',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: savingQuote ? 'not-allowed' : 'pointer',
                    fontSize: '0.8rem'
                  }}
                >
                  ✕ Kapat
                </button>
              </div>
            </div>

            {/* Quote Content */}
            <div style={{ padding: '0' }}>
              <div id="quote-template">
                <QuoteTemplate
                  quoteData={quoteData}
                  isEditable={true}
                  onDataChange={handleQuoteDataUpdate}
                  currentQuoteNumber={latestQuoteNumber}
                />
              </div>
            </div>

            {/* 🔥 YENİ: BACKEND NOTU */}
            <div style={{
              padding: '10px',
              backgroundColor: '#f8f9fa',
              borderTop: '1px solid #e0e0e0',
              fontSize: '10px',
              color: '#666',
              textAlign: 'center'
            }}>
              💡 <strong>Not:</strong> Quote ve PO numaraları backend tarafından otomatik üretilecektir.
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrderDetailPage;